import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/leadUtils";
import { useIsMobile } from "@/hooks/useIsMobile";

const DENSITY_KEY = "ls.delivery.density";
const DENSITY_OPTS = [
  { v: "spacious", l: "Spacious" },
  { v: "auto", l: "Auto" },
  { v: "compact", l: "Compact" },
];
const COMPACT_THRESHOLD = 8;
const FILTERS = ["All", "In progress", "Overdue", "Ready to mark Delivered"];

// Phase 7.0 Screen G — per-filter inactive tint (active state is always pine fill)
const FILTER_TINTS = {
  "Overdue": { text: "#A35C45", border: "#E5C9C0" },
  "Ready to mark Delivered": { text: "#3F6B51", border: "#CFE0D2" },
};

// Phase 7.0 Screen G — status chip palette. Only Shoot done + Delivered get chip
// styling per user spec; other statuses (Confirmed, Cancelled, etc.) render as
// plain ink text to avoid inventing unrequested tints.
const STATUS_CHIPS = {
  "Shoot done": { bg: "#EDE6F2", color: "#6B5B8A" },
  "Delivered":  { bg: "#F4EDDF", color: "#9A7A3C" },
};

// Phase 7.0 Screen G — shared grid template for header row + every card row,
// so headers always align with their columns.
const ROW_GRID = "2fr 130px 1.4fr 160px";

export default function Delivery() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [showCompleted, setShowCompleted] = useState(false);
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) || "auto"; } catch { return "auto"; }
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { try { localStorage.setItem(DENSITY_KEY, density); } catch { /* ignore */ } }, [density]);

  const refresh = async () => {
    setLoading(true);
    const { data } = await api.get("/delivery", { params: { include_completed: showCompleted } });
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [showCompleted]);

  const filtered = useMemo(() => {
    let rows = items;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter((r) => (r.client_name || "").toLowerCase().includes(needle));
    }
    if (filter === "In progress") {
      rows = rows.filter((r) => r.status === "Shoot done" && r.deliverables_delivered < r.deliverables_total);
    } else if (filter === "Overdue") {
      rows = rows.filter((r) => r.deliverables_overdue > 0);
    } else if (filter === "Ready to mark Delivered") {
      rows = rows.filter((r) => r.status === "Shoot done" && r.all_delivered);
    }
    return rows;
  }, [items, q, filter]);

  const useCompact = density === "compact" || (density === "auto" && filtered.length > COMPACT_THRESHOLD);

  return (
    <Layout>
      <div className="fade-up" data-testid="delivery-page">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-6 lg:mb-8 flex-wrap">
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#B08D55",
                marginBottom: 8,
              }}
            >
              EDIT &amp; DELIVERY
            </div>
            <h1
              className="font-display"
              style={{
                fontSize: isMobile ? "clamp(30px, 8.5vw, 52px)" : 52,
                lineHeight: 1.05,
                color: "#22382C",
              }}
              data-testid="delivery-title"
            >
              Delivery
            </h1>
            <div
              style={{ fontSize: 14, color: "#76705F", marginTop: 6 }}
              data-testid="delivery-count"
            >
              {filtered.length} project{filtered.length === 1 ? "" : "s"} in production / delivery
            </div>
          </div>

          {/* Density toggle + search (desktop) — mobile hides the density
              toggle and Show-completed control, moving them into the sort-view
              bottom sheet. */}
          {!isMobile && (
          <div className="flex items-center gap-3">
            <div
              data-testid="density-switcher"
              style={{
                display: "flex",
                background: "#FFFFFF",
                border: "1px solid #E9E2D6",
                borderRadius: 8,
                padding: 4,
                gap: 2,
              }}
            >
              {DENSITY_OPTS.map((o) => {
                const active = density === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => setDensity(o.v)}
                    data-testid={`density-${o.v}`}
                    style={{
                      padding: "6px 12px",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      borderRadius: 6,
                      background: active ? "#22382C" : "transparent",
                      color: active ? "#F4EFE4" : "#A39A8B",
                      transition: "background-color .15s, color .15s",
                    }}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            <div style={{ position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#A39A8B",
                  pointerEvents: "none",
                }}
              />
              <input
                data-testid="delivery-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search client"
                style={{
                  paddingLeft: 34,
                  paddingRight: 12,
                  paddingTop: 9,
                  paddingBottom: 9,
                  fontSize: 13,
                  background: "#FCFAF5",
                  border: "1px solid #E4DCCD",
                  borderRadius: 8,
                  width: 260,
                  color: "#2A2722",
                  outline: "none",
                }}
              />
            </div>
          </div>
          )}
        </div>

        {/* Mobile search + filter icon row */}
        {isMobile && (
          <div className="flex items-center gap-2 mb-3">
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Search
                size={14}
                style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A39A8B", pointerEvents: "none" }}
              />
              <input
                data-testid="delivery-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search client"
                style={{
                  width: "100%",
                  paddingLeft: 34, paddingRight: 12,
                  paddingTop: 11, paddingBottom: 11,
                  fontSize: 13.5,
                  background: "#FCFAF5",
                  border: "1px solid #E4DCCD",
                  borderRadius: 12,
                  color: "#2A2722",
                  outline: "none",
                }}
              />
            </div>
            <button
              onClick={() => setSheetOpen(true)}
              data-testid="delivery-sort-view-btn"
              aria-label="Sort & view"
              style={{
                background: "#FFFDFA",
                border: "1px solid #E4DCCD",
                borderRadius: 12,
                padding: "11px 12px",
                cursor: "pointer",
                color: "#22382C",
                display: "grid", placeItems: "center",
                flexShrink: 0,
              }}
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>
        )}

        {/* Filter pills + show completed. Mobile: horizontal scroll, hide
            Show-completed (moves to the sort-view bottom sheet). */}
        <div
          data-testid="delivery-filters"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: isMobile ? "nowrap" : "wrap",
            overflowX: isMobile ? "auto" : "visible",
            marginBottom: isMobile ? 20 : 28,
            paddingBottom: isMobile ? 4 : 0,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`[data-testid="delivery-filters"]::-webkit-scrollbar { display: none; }`}</style>
          {FILTERS.map((f) => {
            const active = filter === f;
            const tint = FILTER_TINTS[f];
            const inactiveText = tint?.text || "#22382C";
            const inactiveBorder = tint?.border || "#E4DCCD";
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                style={{
                  padding: "9px 17px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 500,
                  background: active ? "#22382C" : "#FFFFFF",
                  color: active ? "#F4EFE4" : inactiveText,
                  border: `1px solid ${active ? "#22382C" : inactiveBorder}`,
                  transition: "background-color .15s, color .15s, border-color .15s",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {f}
              </button>
            );
          })}
          {!isMobile && (
          <label
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "#76705F",
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              data-testid="delivery-show-completed"
            />
            Show completed
          </label>
          )}
        </div>

        {loading ? (
          <div className="kicker" data-testid="delivery-loading">Loading</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="delivery-empty"
            style={{
              background: "#FFFFFF",
              border: "1px solid #EDE5D8",
              borderRadius: 12,
              padding: 56,
              textAlign: "center",
              boxShadow: "0 2px 6px rgba(60,46,20,.05)",
            }}
          >
            <div className="kicker" style={{ marginBottom: 10 }}>NOTHING IN DELIVERY</div>
            <p style={{ fontSize: 14, color: "#76705F" }}>
              When a project moves to <span style={{ fontWeight: 500, color: "#22382C" }}>Shoot done</span>, it lands here.
            </p>
          </div>
        ) : (
          <>
            {/* Column header row — only shown for the card (spacious/auto)
                layout on DESKTOP. Mobile always uses the stacked-card variant. */}
            {!useCompact && !isMobile && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: ROW_GRID,
                  gap: 24,
                  alignItems: "center",
                  padding: "0 22px",
                  marginBottom: 12,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                  color: "#A39A8B",
                }}
              >
                <div>Project</div>
                <div>Status</div>
                <div>Deliverables</div>
                <div style={{ textAlign: "right" }}>Next due</div>
              </div>
            )}

            <div
              data-testid="delivery-list"
              data-density={useCompact ? "compact" : "spacious"}
              style={
                isMobile
                  ? { display: "flex", flexDirection: "column", gap: density === "spacious" ? 14 : density === "compact" ? 8 : 11 }
                  : useCompact
                  ? {
                      background: "#FFFFFF",
                      border: "1px solid #EDE5D8",
                      borderRadius: 12,
                      overflow: "hidden",
                      boxShadow: "0 2px 6px rgba(60,46,20,.05)",
                    }
                  : { display: "flex", flexDirection: "column", gap: 10 }
              }
            >
              {filtered.map((p) =>
                isMobile ? (
                  <DeliveryCardMobile key={p.project_id} p={p} onClick={() => navigate(`/delivery/${p.project_id}`)} />
                ) : useCompact ? (
                  <DeliveryRowCompact key={p.project_id} p={p} onClick={() => navigate(`/delivery/${p.project_id}`)} />
                ) : (
                  <DeliveryCard key={p.project_id} p={p} onClick={() => navigate(`/delivery/${p.project_id}`)} />
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile "Sort & view" bottom sheet — spec Screen G */}
      {isMobile && sheetOpen && (
        <DeliverySortSheet
          density={density}
          setDensity={setDensity}
          showCompleted={showCompleted}
          setShowCompleted={setShowCompleted}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </Layout>
  );
}

function daysSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Math.floor((Date.now() - t) / 86400000);
}

function StatusChip({ status }) {
  const chip = STATUS_CHIPS[status];
  if (chip) {
    return (
      <span
        style={{
          background: chip.bg,
          color: chip.color,
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          padding: "5px 10px",
          borderRadius: 6,
          display: "inline-block",
        }}
      >
        {status}
      </span>
    );
  }
  // Confirmed / Cancelled / others — plain ink text, no invented chip styling.
  return (
    <span style={{ fontSize: 14, color: "#22382C" }}>{status}</span>
  );
}

function DeliveryCard({ p, onClick }) {
  const since = daysSince(p.shoot_completed_at);
  const total = p.deliverables_total || 0;
  const delivered = p.deliverables_delivered || 0;
  const pct = total ? Math.round(100 * delivered / total) : 0;
  const barFill = pct === 100 ? "#3F6B51" : (pct > 0 ? "#B08D55" : "transparent");

  return (
    <div
      onClick={onClick}
      data-testid={`delivery-card-${p.project_id}`}
      style={{
        display: "grid",
        gridTemplateColumns: ROW_GRID,
        gap: 24,
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 12,
        padding: "18px 22px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        cursor: "pointer",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(60,46,20,.12)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* PROJECT */}
      <div style={{ minWidth: 0 }}>
        <div
          className="font-display"
          style={{
            fontSize: 19,
            color: "#22382C",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-testid={`delivery-client-${p.project_id}`}
        >
          {p.client_name}
        </div>
        <div style={{ fontSize: 13, color: "#76705F", marginTop: 4 }}>
          Wedding · {fmtDate(p.wedding_date)}
          {since !== null && (
            <span style={{ marginLeft: 8 }}>· Shot {since} day{since === 1 ? "" : "s"} ago</span>
          )}
        </div>
      </div>

      {/* STATUS */}
      <div>
        <StatusChip status={p.status} />
      </div>

      {/* DELIVERABLES */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#22382C", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>{delivered} / {total} delivered</span>
          {p.deliverables_overdue > 0 && (
            <span
              data-testid={`delivery-overdue-${p.project_id}`}
              style={{
                background: "#F2DBD3",
                color: "#A35C45",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "3px 8px",
                borderRadius: 6,
              }}
            >
              {p.deliverables_overdue} overdue
            </span>
          )}
        </div>
        <div
          style={{
            marginTop: 8,
            height: 6,
            background: "#F1EDE2",
            borderRadius: 4,
            overflow: "hidden",
            maxWidth: 240,
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: barFill,
              transition: "width .25s ease",
            }}
          />
        </div>
      </div>

      {/* NEXT DUE */}
      <div style={{ textAlign: "right", minWidth: 0 }}>
        {p.next_due ? (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#22382C",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {p.next_due.deliverable_type}
            </div>
            <div style={{ fontSize: 13, color: "#76705F", marginTop: 2 }}>
              {fmtDate(p.next_due.due_at)}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: "#76705F" }}>
            {p.all_delivered ? "All delivered" : "—"}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveryRowCompact({ p, onClick }) {
  return (
    <div
      onClick={onClick}
      data-testid={`delivery-row-${p.project_id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1.6fr 0.9fr 0.7fr 1.2fr 1.2fr",
        gap: 12,
        alignItems: "center",
        padding: "10px 16px",
        borderBottom: "1px solid #F0EAE0",
        background: "#FFFFFF",
        cursor: "pointer",
        fontSize: 13,
        transition: "background-color .12s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6EC")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
    >
      <div
        style={{
          color: "#22382C",
          fontWeight: 500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {p.client_name}
      </div>
      <div style={{ fontSize: 12, color: "#76705F" }}>{fmtDate(p.wedding_date)}</div>
      <div style={{ fontSize: 12, color: "#76705F" }}>{p.status}</div>
      <div style={{ fontSize: 12, color: "#76705F" }}>
        {p.deliverables_delivered}/{p.deliverables_total} done
        {p.deliverables_overdue > 0 && (
          <span style={{ marginLeft: 8, color: "#A35C45" }}>· {p.deliverables_overdue} overdue</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#76705F", textAlign: "right" }}>
        {p.next_due ? `${p.next_due.deliverable_type} · ${fmtDate(p.next_due.due_at)}` : (p.all_delivered ? "All delivered" : "—")}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 7.1 Pass 2C — Mobile-only components for the Delivery list.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mobile stacked delivery card (Screen G spec). Same data + click handler as
 * the desktop DeliveryCard, restructured for narrow viewports: project name +
 * status chip top row; subtext (wedding · date · shot N days ago); progress
 * row (delivered/total left, % right) with a thin bar; divider; NEXT DUE
 * label + deliverable + due date + chevron.
 */
function DeliveryCardMobile({ p, onClick }) {
  const since = daysSince(p.shoot_completed_at);
  const total = p.deliverables_total || 0;
  const delivered = p.deliverables_delivered || 0;
  const pct = total ? Math.round(100 * delivered / total) : 0;
  const barFill = pct === 100 ? "#3F6B51" : (pct > 0 ? "#B08D55" : "transparent");
  return (
    <div
      onClick={onClick}
      data-testid={`delivery-card-${p.project_id}`}
      style={{
        background: "#FFFDFA",
        borderRadius: 16,
        boxShadow: "0 4px 14px rgba(34,56,44,0.055), 0 1px 2px rgba(34,56,44,0.04)",
        padding: "14px 14px 12px",
        cursor: "pointer",
      }}
    >
      {/* Row 1: project name + status chip */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 5 }}>
        <div
          className="font-display"
          style={{
            fontSize: 17,
            color: "#22382C",
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          data-testid={`delivery-client-${p.project_id}`}
        >
          {p.client_name}
        </div>
        <div style={{ flexShrink: 0 }}>
          <StatusChip status={p.status} />
        </div>
      </div>
      {/* Row 2: subtext */}
      <div style={{ fontSize: 12, color: "#76705F", marginBottom: 12 }}>
        Wedding · {fmtDate(p.wedding_date)}
        {since !== null && <span> · Shot {since} day{since === 1 ? "" : "s"} ago</span>}
      </div>
      {/* Row 3: progress */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#22382C" }}>
          {delivered} / {total} delivered
        </span>
        <span
          style={{
            fontFamily: "var(--ds-num-font)",
            fontSize: 13,
            fontWeight: 500,
            color: "#22382C",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {pct}%
        </span>
      </div>
      <div style={{ height: 5, background: "#F1EDE2", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: barFill, transition: "width .25s ease" }} />
      </div>
      {p.deliverables_overdue > 0 && (
        <div style={{ marginTop: 8 }}>
          <span
            data-testid={`delivery-overdue-${p.project_id}`}
            style={{
              background: "#F2DBD3", color: "#A35C45",
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", padding: "3px 8px", borderRadius: 6,
            }}
          >
            {p.deliverables_overdue} overdue
          </span>
        </div>
      )}
      {/* Divider + NEXT DUE */}
      <div style={{ borderTop: "1px solid #F0EAE0", margin: "12px 0 10px" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.13em",
              textTransform: "uppercase", color: "#A39A8B", marginBottom: 3,
            }}
          >
            Next due
          </div>
          {p.next_due ? (
            <div style={{ fontSize: 13, color: "#22382C" }}>
              <span style={{ fontWeight: 600 }}>{p.next_due.deliverable_type}</span>
              <span style={{ color: "#76705F", marginLeft: 6 }}>· {fmtDate(p.next_due.due_at)}</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#76705F" }}>
              {p.all_delivered ? "All delivered" : "—"}
            </div>
          )}
        </div>
        <ChevronRight size={16} color="#A39A8B" style={{ flexShrink: 0 }} />
      </div>
    </div>
  );
}

/**
 * Mobile "Sort & view" bottom sheet — density segmented + Show completed
 * toggle. Wraps the existing state (spec §Screen G bottom sheet).
 */
function DeliverySortSheet({ density, setDensity, showCompleted, setShowCompleted, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        data-testid="delivery-sort-scrim"
        style={{
          position: "fixed", inset: 0, zIndex: 10050,
          background: "rgba(20,30,24,0.42)",
          backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="delivery-sort-sheet"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          zIndex: 10051,
          background: "#FFFDFA",
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          boxShadow: "0 -12px 40px rgba(20,30,24,0.28)",
          padding: `18px 20px calc(env(safe-area-inset-bottom, 0px) + 18px)`,
          isolation: "isolate",
        }}
      >
        <div aria-hidden="true" style={{ width: 44, height: 5, background: "rgba(34,56,44,0.18)", borderRadius: 999, margin: "0 auto 14px" }} />
        <div style={{ fontFamily: "var(--ds-num-font)", fontSize: 20, color: "#22382C", marginBottom: 16 }}>
          Sort &amp; view
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B", marginBottom: 8 }}>
          DENSITY
        </div>
        <div
          style={{
            display: "flex",
            background: "#EFE8DA",
            borderRadius: 999,
            padding: 4,
            marginBottom: 20,
          }}
        >
          {DENSITY_OPTS.map((o) => {
            const active = density === o.v;
            return (
              <button
                key={o.v}
                onClick={() => setDensity(o.v)}
                data-testid={`density-${o.v}`}
                style={{
                  flex: 1,
                  padding: "9px 6px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  background: active ? "#22382C" : "transparent",
                  color: active ? "#F4EFE4" : "#76705F",
                  boxShadow: active ? "0 2px 6px rgba(34,56,44,0.2)" : "none",
                  border: "none",
                  cursor: "pointer",
                  transition: "all .18s ease",
                }}
              >
                {o.l}
              </button>
            );
          })}
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "13px 14px",
            background: "#FCFAF5",
            border: "1px solid #E4DCCD",
            borderRadius: 12,
            cursor: "pointer",
            marginBottom: 20,
          }}
        >
          <span style={{ fontSize: 14, color: "#22382C" }}>Show completed</span>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            data-testid="delivery-show-completed"
          />
        </label>
        <button
          onClick={onClose}
          data-testid="delivery-sort-done"
          style={{
            width: "100%",
            padding: "13px 16px",
            background: "#22382C",
            color: "#F4EFE4",
            border: "none",
            borderRadius: 12,
            fontSize: 13.5,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 6px 14px rgba(27,38,31,0.24)",
          }}
        >
          Done
        </button>
      </div>
    </>
  );
}

